import React, { memo, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { AnnotationType } from '../../api/protosCompiled/annotation/annotation_pb';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useKeyBoardEventStore } from '../../stores/keyEventStore';
import {
  useDeleteAnnotationMutation,
  useUpdateShapeAnnotationMutation,
} from './useAnnotationHooks';
import { ShapeEditHandles } from './ShapeEditHandles';
import { EnhancedAnnotation, SVGAnnotationRendererProps } from './types';
import {
  handleRectangleResize,
  handlePolygonMidpointResize,
  useUpdateShape,
  getShapeDefaults,
  getPosition,
  calculateRotationAngle,
  buildTransformString,
  constrainToBounds,
  moveShapeByDelta,
  roundGeometryCoordinates,
  getEffectiveGeometry,
  getEffectiveTransform,
  getAnnotationTypeName,
  calculateArrowHeadGeometry,
  applySnapAngles,
} from './utils';
import { MeasurementOverlay } from './MeasurementOverlay';
import { useAssemblyStore } from '../../stores/AssemblyStore';

export const SVGAnnotationRenderer: React.FC<SVGAnnotationRendererProps> = memo(
  ({ annotation, onHyperlinkClick, onSelect, isSelected, getTargetObjectName }) => {
    // 🚫 COMPREHENSIVE INTERACTION BLOCKING: When isAnnotating=true, ALL existing shapes
    // have their interactions completely disabled to prevent conflicts during annotation creation.
    // This includes: onClick, onMouseDown, onMouseEnter, onMouseLeave, and pointerEvents: 'none'

    const { darkMode } = useTheme();
    const { currentTakeoffAssembly } = useStore(useAssemblyStore);
    const deleteAnnotationMutation = useDeleteAnnotationMutation();
    const updateShape = useUpdateShape();
    const updateShapeMutation = useUpdateShapeAnnotationMutation();
    const shapeSelectionOffset = 5;

    // Text editing state
    const [isEditingText, setIsEditingText] = useState(false);
    const [editingText, setEditingText] = useState('');

    // Get snap settings from stores
    const { angleOptions, showLineMeasurements } = useStore(useUnityBuildStore);
    const { snapMode } = useStore(useKeyBoardEventStore);
    const textInputRef = useRef<HTMLTextAreaElement>(null);

    // Get live editing state from store
    const { useAnnotationStore } = require('../annotations/annotationStore');
    const {
      liveEditingShapeId,
      liveEditingGeometry,
      liveEditingTransform,
      isAnnotating,
      coordinatedDragActive,
      coordinatedDragGeometry,
      coordinatedDragTransform,
    } = useAnnotationStore();
    const isLiveEditing = liveEditingShapeId === annotation.id;
    const isCoordinatedDragging =
      coordinatedDragActive && coordinatedDragGeometry.has(annotation.id);

    // Helper function to check if shape interactions should be disabled
    const shouldDisableInteractions = useCallback(() => {
      const disabled = isAnnotating || !!currentTakeoffAssembly;
      return disabled;
    }, [isAnnotating, currentTakeoffAssembly]);

    // Helper function to get the appropriate pointer events style
    const getPointerEventsStyle = () => {
      if (shouldDisableInteractions()) {
        return { pointerEvents: 'none' as const };
      }
      return {};
    };

    // Handle right-click context menu
    const handleContextMenu = useCallback(
      (e: React.MouseEvent) => {
        // For text annotations that are currently being edited, allow browser context menu
        if (annotation.type === AnnotationType.TEXT && isEditingText) {
          // Don't prevent default - let browser show spell check context menu
          e.stopPropagation();
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        if (shouldDisableInteractions()) return;

        // Open the annotation editor at the cursor position from store
        // No-op: right-click now only opens the editor via store
        const store = (
          require('../annotations/annotationStore') as any
        ).useAnnotationStore?.getState?.();
        if (store?.setEditingAnnotation) {
          store.setEditingAnnotation(annotation.id, annotation);
        } else {
          console.error('❌ Store not found or setEditingAnnotation not available');
        }
      },
      [shouldDisableInteractions, annotation, isEditingText]
    );

    // Initialize editing text when entering edit mode
    useEffect(() => {
      if (isEditingText && textInputRef.current) {
        const currentText = annotation.geometry?.text || 'Click to edit';
        setEditingText(currentText);
        textInputRef.current.focus();
        textInputRef.current.select();
      }
    }, [isEditingText, annotation.geometry?.text]);

    // Auto-enter edit mode for newly created text annotations
    useEffect(() => {
      if (
        annotation.type === AnnotationType.TEXT &&
        annotation.geometry?.text === 'Click to edit' &&
        !isEditingText
      ) {
        // This is a newly created text annotation, auto-enter edit mode
        setIsEditingText(true);
      }
    }, [annotation.type, annotation.geometry?.text, isEditingText]);

    // Handle text editing
    const handleTextEdit = () => {
      if (annotation.type === AnnotationType.TEXT) {
        setIsEditingText(true);
      }
    };

    const handleTextSave = () => {
      if (editingText.trim() === '' || editingText.trim() === 'Click to edit') {
        // Delete empty text annotations or ones that still have the default placeholder text
        deleteAnnotationMutation.mutate({
          id: annotation.id,
          objectId: annotation.objectId,
        });
      } else {
        // Update the text in the annotation - ensure consistency between geometry.text and root text
        const updatedGeometry = {
          ...annotation.geometry,
          text: editingText.trim(),
        };

        updateShape(annotation.id, {
          id: annotation.id,
          objectId: annotation.objectId,
          type: AnnotationType.TEXT,
          geometry: updatedGeometry,
          style: annotation.style,
          transform: annotation.transform, // Preserve existing transform
          layerId: (annotation as any).layerId || 'default',
          zIndex: (annotation as any).zIndex ?? 0,
          isdimensional: annotation.isdimensional, // Preserve dimensional flag during text edit
        });
      }
      setIsEditingText(false);
    };

    const handleTextCancel = () => {
      setEditingText(annotation.geometry?.text || 'Click to edit');
      setIsEditingText(false);
    };

    const handleTextKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleTextSave();
      } else if (e.key === 'Enter' && e.shiftKey) {
        // Allow Shift+Enter for new lines in multi-line text
        // The textarea will handle this naturally
        return;
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleTextCancel();
      }
    };

    // Default styling based on theme
    const defaultStyle = useMemo(
      () => ({
        fill: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        stroke: darkMode ? '#ffffff' : '#000000',
        strokeWidth: '2',
        strokeDasharray: '',
        opacity: '1',
        fontSize: '14',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'normal',
        fontStyle: 'normal',
        textDecoration: 'none',
        textAnchor: 'start',
      }),
      [darkMode]
    );

    // Get shape-specific default colors
    const shapeDefaults = useMemo(() => getShapeDefaults, []);

    // Handle shape selection
    const handleShapeClick = (e: React.MouseEvent) => {
      // CRITICAL: Check for current takeoff assembly FIRST before preventing event propagation
      if (currentTakeoffAssembly) {
        // Allow the event to bubble up to the takeoff system for point addition
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Don't interact if we're in annotation mode
      const annotationStore = (
        require('../annotations/annotationStore') as any
      ).useAnnotationStore?.getState?.();
      if (annotationStore?.isAnnotating) return;

      // Set selected in store (supports multi-select)
      try {
        const store = (
          require('../annotations/annotationStore') as any
        ).useAnnotationStore?.getState?.();

        // Check if we have an existing group selection (annotations + takeoff points)
        const selectionStore = (
          require('../../stores/SelectionStore') as any
        ).useSelectionStore?.getState?.();
        const hasExistingGroup =
          store?.selectedShapeIds?.length > 0 || selectionStore?.selectedPoints?.length > 0;

        if (hasExistingGroup) {
          // If we have an existing group, maintain it and just add/remove this annotation
          const already = (store?.selectedShapeIds || []).includes(annotation.id);
          if (already) {
            store?.removeSelectedShapeId?.(annotation.id);
          } else {
            store?.addSelectedShapeId?.(annotation.id);
          }
        } else if (store?.isMultiSelectMode) {
          // Only toggle in multi-select mode if no existing group
          const already = (store?.selectedShapeIds || []).includes(annotation.id);
          if (already) {
            store?.removeSelectedShapeId?.(annotation.id);
          } else {
            store?.addSelectedShapeId?.(annotation.id);
          }
        } else {
          // Single select mode - clear group and select this annotation
          store?.clearSelectedShapeIds?.();
          store?.setSelectedShapeId?.(annotation.id);
        }
        // Always set current annotation for editor context
        store?.setCurrentAnnotation?.(annotation);
      } catch {}
      if (onSelect) onSelect(annotation);
    };

    // Handle shape dragging
    const handleShapeMouseDown = (e: React.MouseEvent) => {
      // CRITICAL: Check for current takeoff assembly FIRST before preventing event propagation
      if (currentTakeoffAssembly) {
        // Allow the event to bubble up to the takeoff system for point addition
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Don't drag if we're in annotation mode
      const annotationStore = (
        require('../annotations/annotationStore') as any
      ).useAnnotationStore?.getState?.();
      if (annotationStore?.isAnnotating) {
        console.log('Skipping drag - annotation mode active');
        return;
      }

      // Select the shape first
      handleShapeClick(e);

      // Start drag operation
      const svg = ((e.currentTarget as any).ownerSVGElement as SVGSVGElement) || undefined;
      if (!svg) return;

      // Get cursor position from store (document-relative coordinates)
      const unityStore = (require('../../stores/store') as any).useUnityBuildStore?.getState?.();
      const cursorPosition = unityStore?.cursorPosition || { x: 0, y: 0 };

      // Check if we have multiple selected items and broadcast drag start
      const selectionStore = (
        require('../../stores/SelectionStore') as any
      ).useSelectionStore?.getState?.();
      const hasMultipleSelections =
        annotationStore?.selectedShapeIds?.length > 1 ||
        (selectionStore?.selectedPoints?.length > 0 &&
          annotationStore?.selectedShapeIds?.length > 0);

      if (hasMultipleSelections) {
        // Broadcast coordinated drag start event
        try {
          document.dispatchEvent(
            new CustomEvent('coordinated:drag-start', {
              detail: {
                mode: 'group',
                draggedPoints: [], // Annotations don't have point structure
                cursorPos: cursorPosition,
              },
            })
          );
        } catch {}
      }

      // For drag operations, we work with cursor deltas in the store's coordinate system
      // This ensures consistent behavior regardless of zoom level
      const originalGeometry = { ...annotation.geometry };
      let lastCursorPos = cursorPosition; // Track the last cursor position for delta calculations

      // hasMultipleSelections is already defined above for drag start

      // For drag operations, we work directly with cursor deltas in the store's coordinate system
      // This ensures consistent behavior regardless of zoom level by avoiding coordinate transformations
      // The store's cursor position is already document-relative and consistent

      // Helper function to constrain coordinates within SVG bounds using simple approach
      const constrainCurrentToBounds = (dx: number, dy: number) => {
        const annotationType = annotation.type ?? AnnotationType.TEXT;
        return constrainToBounds(dx, dy, originalGeometry, annotationType);
      };

      // Listen for external collision events to stop this drag
      let shouldStopDrag = false;

      const handleCollision = (e: CustomEvent) => {
        shouldStopDrag = true;
      };

      // Listen for collision events
      document.addEventListener('annotation:border-collision', handleCollision as EventListener);

      const onMouseMove = (ev: MouseEvent) => {
        // Check if we should stop drag due to external collision
        if (shouldStopDrag) {
          return;
        }

        // Get current cursor position from store (document-relative coordinates)
        const unityStoreState = (
          require('../../stores/store') as any
        ).useUnityBuildStore?.getState?.();
        const currentCursorPos = unityStoreState?.cursorPosition || { x: 0, y: 0 };

        // Calculate total movement from start position for smooth live preview
        const totalDx = currentCursorPos.x - cursorPosition.x;
        const totalDy = currentCursorPos.y - cursorPosition.y;

        // Update last cursor position
        lastCursorPos = currentCursorPos;

        // Validate movement values - if they're extremely large, skip the update
        if (Math.abs(totalDx) > 10000 || Math.abs(totalDy) > 10000) {
          console.warn('Extreme drag movement detected, skipping live preview:', {
            totalDx,
            totalDy,
            startPos: cursorPosition,
            currentPos: currentCursorPos,
          });
          return;
        }

        const pdfBounds = unityStoreState?.pdfDimensions;
        const annotationType = annotation.type ?? AnnotationType.TEXT;

        // Broadcast coordinated drag event if we have multiple selections
        if (hasMultipleSelections) {
          // For multi-selections, delegate collision checking to coordinated drag
          try {
            document.dispatchEvent(
              new CustomEvent('coordinated:drag-update', {
                detail: {
                  offset: { x: totalDx, y: totalDy },
                  draggedPoints: [], // Annotations don't have point structure
                  cursorPos: currentCursorPos,
                },
              })
            );
          } catch {}

          // Still update this annotation's live editing state for visual feedback
          const newGeometry = moveShapeByDelta(
            totalDx,
            totalDy,
            originalGeometry,
            annotationType,
            pdfBounds
          );

          const store = (
            require('../annotations/annotationStore') as any
          ).useAnnotationStore?.getState?.();
          store?.setLiveEditingShape?.(annotation.id, newGeometry, annotation.transform);

          // CRITICAL: Also add this dragged annotation to the coordinated drag geometry
          // so it gets saved with the group
          if (store?.coordinatedDragActive && store?.coordinatedDragGeometry) {
            store.coordinatedDragGeometry.set(annotation.id, newGeometry);
          }

          return;
        }

        // Clamp movement within bounds when provided
        const newGeometry = moveShapeByDelta(
          totalDx,
          totalDy,
          originalGeometry,
          annotationType,
          pdfBounds
        );

        // Update live editing state for visual feedback
        const store = (
          require('../annotations/annotationStore') as any
        ).useAnnotationStore?.getState?.();
        store?.setLiveEditingShape?.(annotation.id, newGeometry, annotation.transform); // TEXT ANNOTATION: Preserve transform during drag
      };

      const onMouseUp = () => {
        // Remove collision event listener
        document.removeEventListener(
          'annotation:border-collision',
          handleCollision as EventListener
        );

        // Reset collision state
        shouldStopDrag = false;

        // Calculate final movement from start position (same as live preview)
        const dx = lastCursorPos.x - cursorPosition.x;
        const dy = lastCursorPos.y - cursorPosition.y;

        // Don't clear live editing state immediately - let the mutation handle it
        // This prevents the race condition where the component re-renders with old data
        // before the backend response arrives

        // Only update if there was actual movement and coordinates are reasonable
        // Also only update if this is a single annotation drag (not part of multi-selection)
        if (
          (Math.abs(dx) > 1 || Math.abs(dy) > 1) &&
          Math.abs(dx) < 10000 &&
          Math.abs(dy) < 10000 &&
          !hasMultipleSelections
        ) {
          // Get EXACT geometry from live editing state (what user sees on screen)
          const store = (
            require('../annotations/annotationStore') as any
          ).useAnnotationStore?.getState?.();
          const exactGeometry = store?.liveEditingGeometry || constrainCurrentToBounds(dx, dy);

          // Round the coordinates for final save
          const roundedGeometry = roundGeometryCoordinates(exactGeometry);

          // Update the annotation
          if (Object.keys(roundedGeometry).length > 0) {
            // Final safety check: ensure text is preserved for TEXT annotations
            if (
              annotation.type === AnnotationType.TEXT &&
              annotation.geometry?.text &&
              !roundedGeometry.text
            ) {
              roundedGeometry.text = annotation.geometry.text;
            }

            updateShape(annotation.id, {
              id: annotation.id,
              objectId: annotation.objectId,
              type: annotation.type ?? AnnotationType.TEXT, // Always preserve original type, default to TEXT
              geometry: roundedGeometry,
              style: annotation.style, // Preserve style
              transform: annotation.transform, // Preserve transform during drag
              layerId: (annotation as any).layerId || 'default',
              zIndex: (annotation as any).zIndex ?? 0,
              isdimensional: annotation.isdimensional, // Preserve dimensional flag during drag
            });
          }
        }

        // Broadcast coordinated drag end event if we had multiple selections
        if (hasMultipleSelections) {
          try {
            const annStore = (
              require('../annotations/annotationStore') as any
            ).useAnnotationStore?.getState?.();
            let selectedIds: string[] = annStore?.selectedShapeIds || [];

            // CRITICAL: Ensure the dragged annotation is always included in the save list
            if (!selectedIds.includes(annotation.id)) {
              selectedIds = [...selectedIds, annotation.id];
            }

            document.dispatchEvent(
              new CustomEvent('coordinated:drag-end', {
                detail: {
                  offset: { x: dx, y: dy },
                  selectedIds,
                  primaryDraggedId: annotation.id, // Explicitly mark the primary dragged annotation
                  finalPositions: [
                    { pointId: annotation.id, finalX: lastCursorPos.x, finalY: lastCursorPos.y },
                  ],
                  draggedPoints: [],
                },
              })
            );
          } catch {}
        }

        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    };

    // Get interactive style
    const getInteractiveStyle = (): React.CSSProperties => {
      return {
        cursor: isSelected ? 'move' : 'pointer',
        pointerEvents: isSelected ? 'auto' : 'stroke',
      };
    };

    // Merge default style with annotation style
    const mergedStyle = useMemo(() => {
      const annotationType = annotation.type ?? AnnotationType.TEXT;
      const shapeDefaultsResult = shapeDefaults(annotationType);

      return {
        ...defaultStyle,
        ...shapeDefaultsResult,
        strokeDasharray: annotation.style?.strokedasharray || defaultStyle.strokeDasharray,
        strokeWidth:
          annotation.style?.strokewidth ||
          shapeDefaultsResult.strokeWidth ||
          defaultStyle.strokeWidth,
        fontSize: annotation.style?.fontsize || defaultStyle.fontSize,
        fontFamily: annotation.style?.fontfamily || defaultStyle.fontFamily,
        fontWeight: annotation.style?.fontweight || defaultStyle.fontWeight,
        textAnchor: annotation.style?.textanchor || defaultStyle.textAnchor,
        ...annotation.style,
      };
    }, [defaultStyle, annotation.style, annotation.type, shapeDefaults]);

    // Get position from geometry or fallback to legacy fields - use live editing if available
    const getCurrentPosition = () => {
      let geometry = annotation.geometry;
      if (isCoordinatedDragging && coordinatedDragGeometry.has(annotation.id)) {
        geometry = coordinatedDragGeometry.get(annotation.id);
      } else if (isLiveEditing && liveEditingGeometry) {
        geometry = liveEditingGeometry;
      }
      return getPosition(geometry, annotation.x, annotation.y);
    };

    // Get effective geometry - use coordinated drag, live editing, or original geometry
    const getCurrentEffectiveGeometry = () => {
      if (isCoordinatedDragging && coordinatedDragGeometry.has(annotation.id)) {
        return coordinatedDragGeometry.get(annotation.id);
      }
      return getEffectiveGeometry(liveEditingGeometry, annotation.geometry, isLiveEditing);
    };

    // Helper function to create edit state for any operation
    const createEditState = (
      mode: string,
      start: { x: number; y: number },
      original: EnhancedAnnotation
    ) => {
      (window as any).__shapeEdit = {
        mode,
        start,
        last: start,
        original,
      };
    };

    // Helper function to update edit state during drag
    const updateEditState = (pos: { x: number; y: number }) => {
      (window as any).__shapeEdit = { ...(window as any).__shapeEdit, last: pos };
    };

    // Helper function to get current edit state
    const getEditState = () => (window as any).__shapeEdit;

    // Helper function to clear edit state
    const clearEditState = () => {
      (window as any).__shapeEdit = null;
    };

    // Build transform string
    const getTransform = () => {
      let effectiveTransform = annotation.transform;
      if (isCoordinatedDragging && coordinatedDragTransform.has(annotation.id)) {
        effectiveTransform = coordinatedDragTransform.get(annotation.id);
      } else {
        effectiveTransform = getEffectiveTransform(
          liveEditingTransform,
          annotation.transform,
          isLiveEditing
        );
      }
      const effectiveGeometry = getCurrentEffectiveGeometry();
      const annotationType = annotation.type ?? AnnotationType.TEXT;
      return buildTransformString(
        effectiveTransform,
        effectiveGeometry,
        annotationType,
        isLiveEditing || isCoordinatedDragging
      );
    };

    // Render based on annotation type
    const renderAnnotation = () => {
      const pos = getCurrentPosition();
      const transform = getTransform();

      // Handle backward compatibility - if no type is specified, assume text
      const annotationType = annotation.type ?? AnnotationType.TEXT;

      switch (annotationType) {
        case AnnotationType.HYPERLINK: {
          // Render a small clickable link icon box that navigates to target object
          const effectiveGeometry = getCurrentEffectiveGeometry();
          const pos = getCurrentPosition();
          const width = parseFloat(effectiveGeometry?.width || '24');
          const height = parseFloat(effectiveGeometry?.height || '24');
          const transform = getTransform();

          const targetObjectId = effectiveGeometry?.hyperlinkdata?.targetobjectid;

          const onNavigate = () => {
            if (onHyperlinkClick && targetObjectId) {
              onHyperlinkClick(String(targetObjectId));
            }
          };

          return (
            <g>
              {/* Edge interaction overlay: select/drag/resize (no rotation) */}
              <rect
                x={pos.x}
                y={pos.y}
                width={width}
                height={height}
                fill="none"
                stroke="transparent"
                strokeWidth={shapeSelectionOffset * 2}
                opacity={0}
                transform={transform}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  cursor: shouldDisableInteractions() ? 'default' : isSelected ? 'move' : 'pointer',
                  pointerEvents: shouldDisableInteractions() ? 'none' : 'stroke',
                }}
              />
              {/* Visible hyperlink (purely visual) */}
              <rect
                x={pos.x}
                y={pos.y}
                width={width}
                height={height}
                fill={mergedStyle.fill || 'rgba(33, 150, 243, 0.12)'}
                stroke={mergedStyle.stroke || '#2196f3'}
                strokeWidth={mergedStyle.strokeWidth}
                opacity={mergedStyle.opacity}
                transform={transform}
                style={{ pointerEvents: 'none' }}
              />

              {/* Center interaction overlay: navigate when not selected */}
              {(() => {
                const innerPadding = Math.max(4, shapeSelectionOffset);
                const innerW = Math.max(0, width - innerPadding * 2);
                const innerH = Math.max(0, height - innerPadding * 2);
                return (
                  <rect
                    x={pos.x + innerPadding}
                    y={pos.y + innerPadding}
                    width={innerW}
                    height={innerH}
                    fill="transparent"
                    opacity={0}
                    transform={transform}
                    onClick={
                      shouldDisableInteractions()
                        ? undefined
                        : e => {
                            // Block navigation until edge interaction selects the shape
                            if (!isSelected) {
                              handleShapeClick(e);
                              return;
                            }
                            onNavigate();
                          }
                    }
                    style={{
                      cursor: shouldDisableInteractions()
                        ? 'default'
                        : isSelected
                          ? 'move'
                          : 'pointer',
                      pointerEvents: shouldDisableInteractions() ? 'none' : 'all',
                    }}
                  >
                    {targetObjectId && getTargetObjectName && (
                      <title>{`Navigate to ${getTargetObjectName(targetObjectId)}`}</title>
                    )}
                  </rect>
                );
              })()}

              {/* Separate selection outline - doesn't affect the shape */}
              {isSelected && (
                <rect
                  x={pos.x - 2}
                  y={pos.y - 2}
                  width={width + 4}
                  height={height + 4}
                  fill="none"
                  stroke="rgba(128, 128, 128, 0.8)"
                  strokeWidth="1.5"
                  strokeDasharray="3,3"
                  opacity="1"
                  transform={transform}
                  style={{ pointerEvents: 'none' }}
                />
              )}

              {/* Indicate it's a link with a chain icon-like glyph */}
              <text
                x={pos.x + width / 2}
                y={pos.y + height / 2}
                transform={transform}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  pointerEvents: 'none',
                  fontSize: Math.max(12, Math.min(120, Math.min(width, height) * 0.6)),
                  fontFamily: 'Segoe UI, Arial, sans-serif',
                  fontWeight: 600,
                  fill: String(mergedStyle.stroke || '#1976d2'),
                }}
              >
                ↔
              </text>

              {/* HYPERLINK ANNOTATION - Resize handles but no rotation */}
              <ShapeEditHandles
                annotation={annotation}
                isSelected={isSelected || false}
                transform={transform}
                liveEditingTransform={isLiveEditing ? liveEditingTransform : undefined}
                liveEditingGeometry={isLiveEditing ? liveEditingGeometry : undefined}
                onResizeStart={(handleId, start) => {
                  createEditState('resize', start, annotation);
                  (window as any).__shapeEdit.handleId = handleId; // Store handleId for resize operations
                }}
                onResizeDrag={(handleId, pos) => {
                  updateEditState(pos);
                  // Live preview during resize
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;

                  // Use helper function for consistent resize logic
                  const newGeometry = handleRectangleResize(handleId, start, last, orig);

                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(
                    annotation.id,
                    {
                      ...orig.geometry, // Preserve all existing geometry including hyperlinkdata
                      ...newGeometry,
                    },
                    orig.transform
                  ); // HYPERLINK: Preserve transform during live editing
                }}
                onResizeEnd={() => {
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const handleId = edit.handleId as string;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };

                  // Use helper function for consistent resize logic
                  const newGeometry = handleRectangleResize(handleId, start, last, orig);

                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: AnnotationType.HYPERLINK,
                    geometry: {
                      ...orig.geometry, // Preserve all existing geometry including hyperlinkdata
                      x: String(Math.round(parseFloat(newGeometry.x))),
                      y: String(Math.round(parseFloat(newGeometry.y))),
                      width: String(Math.round(parseFloat(newGeometry.width))),
                      height: String(Math.round(parseFloat(newGeometry.height))),
                    },
                    style: orig.style,
                    transform: orig.transform, // Preserve the transform during resize
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during resize
                  });
                  clearEditState();
                }}
                // No rotation handlers for hyperlinks
                onRotateStart={undefined}
                onRotateDrag={undefined}
                onRotateEnd={undefined}
              />
            </g>
          );
        }
        // Note: The legacy TextAnnotationHandler path is removed. All TEXT renders below.

        case AnnotationType.TEXT: {
          // TEXT ANNOTATION CASE - Unique identifier for this specific case
          const effectiveGeometry = getCurrentEffectiveGeometry();
          const width = parseFloat(effectiveGeometry?.width || '200');
          const height = parseFloat(effectiveGeometry?.height || '100');
          const text = effectiveGeometry?.text || 'Click to edit';
          // Leaders support (optional) - use live editing geometry if available
          const currentGeometry = getCurrentEffectiveGeometry();
          const leaders: any[] =
            (currentGeometry?.leadersList as any[]) || (currentGeometry?.leaders as any[]) || [];

          // Helper: compute anchor absolute coords from box and LeaderAnchor enum numeric
          const getAnchorPositionForLeader = (anchorNum: number) => {
            const ax = pos.x;
            const ay = pos.y;
            const w = width;
            const h = height;
            switch (anchorNum) {
              case 1 /* NW */:
                return { x: ax, y: ay };
              case 2 /* N */:
                return { x: ax + w / 2, y: ay };
              case 3 /* NE */:
                return { x: ax + w, y: ay };
              case 4 /* E */:
                return { x: ax + w, y: ay + h / 2 };
              case 5 /* SE */:
                return { x: ax + w, y: ay + h };
              case 6 /* S */:
                return { x: ax + w / 2, y: ay + h };
              case 7 /* SW */:
                return { x: ax, y: ay + h };
              case 8 /* W */:
                return { x: ax, y: ay + h / 2 };
              case 9: /* C */
              default:
                return { x: ax + w / 2, y: ay + h / 2 };
            }
          };

          return (
            <g>
              {/* Invisible thicker stroke for easier interaction */}
              <rect
                x={pos.x}
                y={pos.y}
                width={width + shapeSelectionOffset}
                height={height + shapeSelectionOffset}
                fill="none"
                stroke="transparent"
                strokeWidth={shapeSelectionOffset * 2}
                opacity={0}
                transform={transform}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={
                  shouldDisableInteractions() ||
                  (annotation.type === AnnotationType.TEXT && isEditingText)
                    ? undefined
                    : handleContextMenu
                }
                style={{
                  cursor: shouldDisableInteractions() ? 'default' : isSelected ? 'move' : 'pointer',
                  pointerEvents: shouldDisableInteractions() ? 'none' : 'stroke',
                }}
              />
              {/* Text note background */}
              <rect
                x={pos.x}
                y={pos.y}
                width={width}
                height={height}
                fill={mergedStyle.fill}
                stroke={mergedStyle.stroke}
                strokeWidth={mergedStyle.strokeWidth}
                strokeDasharray={mergedStyle.strokeDasharray}
                opacity={mergedStyle.opacity}
                transform={transform}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={
                  shouldDisableInteractions() ||
                  (annotation.type === AnnotationType.TEXT && isEditingText)
                    ? undefined
                    : handleContextMenu
                }
                style={{
                  ...getInteractiveStyle(),
                  ...getPointerEventsStyle(),
                }}
              />
              {annotation.isdimensional && showLineMeasurements && (
                <MeasurementOverlay
                  type={annotation.type}
                  geometry={getCurrentEffectiveGeometry()}
                  transform={transform}
                  color={String(mergedStyle.stroke || '#1976d2')}
                />
              )}

              {/* Separate selection outline - doesn't affect the shape */}
              {isSelected && (
                <rect
                  x={pos.x - 2}
                  y={pos.y - 2}
                  width={width + 4}
                  height={height + 4}
                  fill="none"
                  stroke="rgba(128, 128, 128, 0.8)"
                  strokeWidth="1.5"
                  strokeDasharray="3,3"
                  opacity="1"
                  transform={transform}
                  style={{ pointerEvents: 'none' }}
                />
              )}
              {/* Text content - editable when selected */}
              <foreignObject
                x={pos.x + 10}
                y={pos.y + 10}
                width={width - 20}
                height={height - 20}
                transform={transform}
                style={{
                  overflow: 'visible',
                  pointerEvents: shouldDisableInteractions() ? 'none' : 'auto',
                }}
              >
                {isEditingText ? (
                  <textarea
                    ref={textInputRef}
                    value={editingText}
                    onChange={e => setEditingText(e.target.value)}
                    onKeyDown={handleTextKeyDown}
                    onBlur={handleTextSave}
                    onContextMenu={e => {
                      // Allow the browser's default context menu to show
                      e.stopPropagation();
                      // Don't call e.preventDefault() - let browser handle it
                    }}
                    spellCheck={true}
                    autoComplete="on"
                    autoCorrect="on"
                    autoCapitalize="sentences"
                    data-gramm="true"
                    data-gramm_editor="true"
                    data-testid="annotation-textarea"
                    inputMode="text"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: '2px solid #1976d2',
                      borderRadius: '4px',
                      outline: 'none',
                      fontSize: Number(mergedStyle.fontSize) || 14,
                      fontFamily: String(mergedStyle.fontFamily || 'Arial, sans-serif'),
                      fontWeight: String(mergedStyle.fontWeight || 'normal'),
                      fontStyle: String((mergedStyle as any).fontStyle || 'normal'),
                      textDecoration: String((mergedStyle as any).textDecoration || 'none'),
                      color: String(mergedStyle.stroke || '#333'),
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      padding: '8px',
                      margin: '0',
                      resize: 'none',
                      textAlign:
                        (mergedStyle.textAnchor as any) === 'end'
                          ? 'right'
                          : (mergedStyle.textAnchor as any) === 'middle'
                            ? 'center'
                            : 'left',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      lineHeight: '1.4',
                      verticalAlign: 'top',
                      overflow: 'visible',
                      WebkitUserSelect: 'text',
                      userSelect: 'text',
                      cursor: 'text',
                    }}
                    placeholder="Enter your note here..."
                  />
                ) : (
                  <div
                    onClick={
                      shouldDisableInteractions()
                        ? undefined
                        : e => {
                            e.stopPropagation(); // Prevent event bubbling to parent handlers
                            handleTextEdit();
                          }
                    }
                    onDoubleClick={
                      shouldDisableInteractions()
                        ? undefined
                        : e => {
                            e.stopPropagation(); // Prevent event bubbling to parent handlers
                            handleTextEdit();
                          }
                    }
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      fontSize: Number(mergedStyle.fontSize) || 14,
                      fontFamily: String(mergedStyle.fontFamily || 'Arial, sans-serif'),
                      fontWeight: String(mergedStyle.fontWeight || 'normal'),
                      fontStyle: String((mergedStyle as any).fontStyle || 'normal'),
                      textDecoration: String((mergedStyle as any).textDecoration || 'none'),
                      color: String(mergedStyle.stroke || '#333'),
                      lineHeight: '1.4',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                      padding: '8px',
                      margin: '0',
                      cursor: shouldDisableInteractions() ? 'default' : 'text',
                      userSelect: shouldDisableInteractions() ? 'none' : 'text',
                      textAlign:
                        (mergedStyle.textAnchor as any) === 'end'
                          ? 'right'
                          : (mergedStyle.textAnchor as any) === 'middle'
                            ? 'center'
                            : 'left',
                      transition: 'background-color 0.2s ease, border 0.2s ease',
                      overflow: 'hidden',
                      border: '1px solid transparent',
                      borderRadius: '4px',
                    }}
                    onMouseEnter={
                      shouldDisableInteractions()
                        ? undefined
                        : e => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.3)';
                          }
                    }
                    onMouseLeave={
                      shouldDisableInteractions()
                        ? undefined
                        : e => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.border = '1px solid transparent';
                          }
                    }
                  >
                    {text}
                  </div>
                )}
              </foreignObject>

              {/* Leaders rendering (anchor -> pivot -> end with arrowhead) */}
              {Array.isArray(leaders) &&
                leaders.map((leader, idx) => {
                  // Use live editing geometry if available for this leader
                  const effectiveLeader =
                    isLiveEditing && liveEditingGeometry?.leaders?.[idx]
                      ? liveEditingGeometry.leaders[idx]
                      : leader;
                  const anchorNum: number = Number(effectiveLeader.anchor ?? 6);
                  const anchorPos = getAnchorPositionForLeader(anchorNum);
                  const piv = effectiveLeader.pivot || {};
                  const end = effectiveLeader.end || {};
                  const px = parseFloat(piv.x ?? String(anchorPos.x));
                  const py = parseFloat(piv.y ?? String(anchorPos.y));
                  const ex = parseFloat(end.x ?? String(px + 120));
                  const ey = parseFloat(end.y ?? String(py));
                  const strokeColor =
                    (leader.style?.stroke as string) || (mergedStyle.stroke as any);
                  const strokeWidth = parseFloat(
                    (leader.style?.strokewidth as string) || String(mergedStyle.strokeWidth || '2')
                  );
                  const dash =
                    (leader.style?.strokedasharray as string) || mergedStyle.strokeDasharray;
                  const arrowSize = Math.max(
                    6,
                    Math.min(24, parseFloat((effectiveLeader.arrowheadsize as string) || '10'))
                  );

                  // Calculate arrow head using utility function
                  const leaderArrowGeometry = calculateArrowHeadGeometry(px, py, ex, ey, arrowSize);
                  const {
                    lineEndX: leaderLineEndX,
                    lineEndY: leaderLineEndY,
                    arrowHead1X: ah1x,
                    arrowHead1Y: ah1y,
                    arrowHead2X: ah2x,
                    arrowHead2Y: ah2y,
                  } = leaderArrowGeometry;

                  // Drag handlers - EXACT same pattern as text box dragging
                  const startLeaderDrag = (
                    which: 'pivot' | 'end',
                    startEvent: React.MouseEvent<SVGCircleElement, MouseEvent>
                  ) => {
                    startEvent.preventDefault();
                    startEvent.stopPropagation();

                    // Get starting cursor position - same as text box
                    const unityStore = (
                      require('../../stores/store') as any
                    ).useUnityBuildStore?.getState?.();
                    const startCursor = unityStore?.cursorPosition || { x: 0, y: 0 };
                    let lastCursor = startCursor;

                    // Store original geometry - same as text box
                    const originalGeometry = { ...currentGeometry };

                    const onMove = () => {
                      // Get current cursor position - same as text box
                      const s = (
                        require('../../stores/store') as any
                      ).useUnityBuildStore?.getState?.();
                      const currentCursor = s?.cursorPosition || { x: 0, y: 0 };

                      // Calculate total movement from start - same as text box
                      const totalDx = currentCursor.x - startCursor.x;
                      const totalDy = currentCursor.y - startCursor.y;
                      lastCursor = currentCursor;

                      // Apply delta to original leader - same pattern as text box
                      const originalLeaders =
                        originalGeometry.leaders || originalGeometry.leadersList || [];
                      const updatedLeaders = [...originalLeaders];
                      const originalLeader = originalLeaders[idx];

                      if (which === 'pivot') {
                        const originalPx = parseFloat(originalLeader.pivot?.x ?? String(px));
                        const originalPy = parseFloat(originalLeader.pivot?.y ?? String(py));
                        const newPivotPoint = { x: originalPx + totalDx, y: originalPy + totalDy };

                        // Apply snap angles: pivot snaps relative to anchor
                        const anchorPoint = { x: anchorPos.x, y: anchorPos.y };
                        const snappedPivotPoint = applySnapAngles(
                          anchorPoint,
                          newPivotPoint,
                          snapMode,
                          angleOptions
                        );

                        updatedLeaders[idx] = {
                          ...originalLeader,
                          pivot: {
                            x: String(snappedPivotPoint.x),
                            y: String(snappedPivotPoint.y),
                          },
                        };
                      } else {
                        const originalEx = parseFloat(originalLeader.end?.x ?? String(ex));
                        const originalEy = parseFloat(originalLeader.end?.y ?? String(ey));
                        const newEndPoint = { x: originalEx + totalDx, y: originalEy + totalDy };

                        // Apply snap angles: end snaps relative to pivot
                        const pivotPoint = { x: px, y: py };
                        const snappedEndPoint = applySnapAngles(
                          pivotPoint,
                          newEndPoint,
                          snapMode,
                          angleOptions
                        );

                        updatedLeaders[idx] = {
                          ...originalLeader,
                          end: {
                            x: String(snappedEndPoint.x),
                            y: String(snappedEndPoint.y),
                          },
                        };
                      }

                      // Update live editing state - same as text box
                      const store = (
                        require('../annotations/annotationStore') as any
                      ).useAnnotationStore?.getState?.();
                      store?.setLiveEditingShape?.(
                        annotation.id,
                        { ...originalGeometry, leaders: updatedLeaders },
                        annotation.transform
                      );
                    };

                    const onUp = () => {
                      window.removeEventListener('mousemove', onMove);
                      window.removeEventListener('mouseup', onUp);

                      // Calculate final movement - same as text box
                      const finalDx = lastCursor.x - startCursor.x;
                      const finalDy = lastCursor.y - startCursor.y;

                      // Only update if there was movement - same as text box
                      if (Math.abs(finalDx) > 1 || Math.abs(finalDy) > 1) {
                        const originalLeaders =
                          originalGeometry.leaders || originalGeometry.leadersList || [];
                        const finalLeaders = [...originalLeaders];
                        const originalLeader = originalLeaders[idx];

                        if (which === 'pivot') {
                          const originalPx = parseFloat(originalLeader.pivot?.x ?? String(px));
                          const originalPy = parseFloat(originalLeader.pivot?.y ?? String(py));
                          const newPivotPoint = {
                            x: originalPx + finalDx,
                            y: originalPy + finalDy,
                          };

                          // Apply snap angles: pivot snaps relative to anchor
                          const anchorPoint = { x: anchorPos.x, y: anchorPos.y };
                          const snappedPivotPoint = applySnapAngles(
                            anchorPoint,
                            newPivotPoint,
                            snapMode,
                            angleOptions
                          );

                          finalLeaders[idx] = {
                            ...originalLeader,
                            pivot: {
                              x: String(snappedPivotPoint.x),
                              y: String(snappedPivotPoint.y),
                            },
                          };
                        } else {
                          const originalEx = parseFloat(originalLeader.end?.x ?? String(ex));
                          const originalEy = parseFloat(originalLeader.end?.y ?? String(ey));
                          const newEndPoint = { x: originalEx + finalDx, y: originalEy + finalDy };

                          // Apply snap angles: end snaps relative to pivot
                          const pivotPoint = { x: px, y: py };
                          const snappedEndPoint = applySnapAngles(
                            pivotPoint,
                            newEndPoint,
                            snapMode,
                            angleOptions
                          );

                          finalLeaders[idx] = {
                            ...originalLeader,
                            end: {
                              x: String(snappedEndPoint.x),
                              y: String(snappedEndPoint.y),
                            },
                          };
                        }

                        // Use mutation directly to control live editing state - same as text box
                        updateShapeMutation.mutate(
                          {
                            id: annotation.id,
                            objectId: annotation.objectId,
                            type: AnnotationType.TEXT,
                            geometry: { ...originalGeometry, leaders: finalLeaders },
                            style: annotation.style,
                            transform: annotation.transform,
                            layerId: (annotation as any).layerId || 'default',
                            zIndex: (annotation as any).zIndex ?? 0,
                          },
                          {
                            onSuccess: () => {
                              // Clear live editing state only after successful update
                              const store = (
                                require('../annotations/annotationStore') as any
                              ).useAnnotationStore?.getState?.();
                              store?.setLiveEditingShape?.(null);
                            },
                            onError: () => {
                              // Clear on error too
                              const store = (
                                require('../annotations/annotationStore') as any
                              ).useAnnotationStore?.getState?.();
                              store?.setLiveEditingShape?.(null);
                            },
                          }
                        );
                      }
                    };

                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  };

                  return (
                    <g key={`leader-${annotation.id}-${idx}`} transform={transform}>
                      {/* Always show the leader - use effectiveLeader for live updates */}
                      <polyline
                        points={`${anchorPos.x},${anchorPos.y} ${px},${py} ${leaderLineEndX},${leaderLineEndY}`}
                        fill="none"
                        stroke={strokeColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={dash}
                        style={{ pointerEvents: 'none' }}
                      />
                      {/* Arrowhead */}
                      {(effectiveLeader.showarrowhead ?? true) && (
                        <polygon
                          points={`${ex},${ey} ${ah1x},${ah1y} ${ah2x},${ah2y}`}
                          fill={strokeColor}
                          style={{ pointerEvents: 'none' }}
                        />
                      )}
                      {/* Drag handles (only when selected) */}
                      {isSelected && !shouldDisableInteractions() && (
                        <g>
                          <circle
                            cx={px}
                            cy={py}
                            r={6}
                            fill="#ffffff"
                            stroke={strokeColor}
                            strokeWidth={2}
                            style={{ cursor: 'move' }}
                            onMouseDown={e => startLeaderDrag('pivot', e)}
                          />
                          <circle
                            cx={ex}
                            cy={ey}
                            r={6}
                            fill="#ffffff"
                            stroke={strokeColor}
                            strokeWidth={2}
                            style={{ cursor: 'move' }}
                            onMouseDown={e => startLeaderDrag('end', e)}
                          />
                        </g>
                      )}
                    </g>
                  );
                })}

              {/* Live leader preview and finalize interaction when adding a leader */}
              {(() => {
                const storeState = (
                  require('../annotations/annotationStore') as any
                ).useAnnotationStore?.getState?.();
                const unity = (
                  require('../../stores/store') as any
                ).useUnityBuildStore?.getState?.();
                const cursor = unity?.cursorPosition;
                const active =
                  !!storeState?.isAddingLeader && storeState?.addingLeaderForId === annotation.id;
                if (!active || !cursor) return null;

                // Choose an anchor based on cursor location relative to box
                const cx = cursor.x;
                const cy = cursor.y;
                const left = cx < pos.x;
                const right = cx > pos.x + width;
                const above = cy < pos.y;
                const below = cy > pos.y + height;
                let anchorNum = 6; // default South
                if (above && left)
                  anchorNum = 1; // NW
                else if (above && right)
                  anchorNum = 3; // NE
                else if (below && left)
                  anchorNum = 7; // SW
                else if (below && right)
                  anchorNum = 5; // SE
                else if (left)
                  anchorNum = 8; // W
                else if (right)
                  anchorNum = 4; // E
                else if (above)
                  anchorNum = 2; // N
                else if (below) anchorNum = 6; // S

                const aPos = getAnchorPositionForLeader(anchorNum);
                // Consistent hinge point for preview (matches creation)
                const pvx = (aPos.x + cx) / 2;
                const pvy = (aPos.y + cy) / 2;
                const strokeColor = (mergedStyle.stroke as any) || '#1976d2';
                const strokeWidth = parseFloat(String(mergedStyle.strokeWidth || '2'));
                const dash = mergedStyle.strokeDasharray as any;

                const finalize = (e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const newLeader = {
                    anchor: anchorNum,
                    pivot: { x: String((aPos.x + cx) / 2), y: String((aPos.y + cy) / 2) },
                    end: { x: String(cx), y: String(cy) },
                    showarrowhead: true,
                    arrowheadsize: '10',
                    style: {},
                  } as any;
                  const existing: any[] =
                    (currentGeometry?.leaders as any[]) ||
                    (currentGeometry?.leadersList as any[]) ||
                    [];
                  const newList = [...existing, newLeader];
                  updateShape(annotation.id, {
                    id: annotation.id,
                    objectId: annotation.objectId,
                    type: AnnotationType.TEXT,
                    geometry: { ...currentGeometry, leaders: newList },
                    style: annotation.style,
                    transform: annotation.transform,
                    layerId: (annotation as any).layerId || 'default',
                    zIndex: (annotation as any).zIndex ?? 0,
                    isdimensional: annotation.isdimensional, // Preserve dimensional flag during leader addition
                  });
                  storeState?.stopAddLeader?.();
                };

                return (
                  <g>
                    {/* Preview polyline with hinge point */}
                    <polyline
                      points={`${aPos.x},${aPos.y} ${pvx},${pvy} ${cx},${cy}`}
                      fill="none"
                      stroke={strokeColor}
                      strokeWidth={strokeWidth}
                      strokeDasharray={dash}
                      transform={transform}
                      style={{ pointerEvents: 'none' }}
                    />
                    {/* Preview arrowhead */}
                    {(() => {
                      const angle = Math.atan2(cy - pvy, cx - pvx);
                      const arrowSize = 10;
                      const ah1x = cx - arrowSize * Math.cos(angle - Math.PI / 6);
                      const ah1y = cy - arrowSize * Math.sin(angle - Math.PI / 6);
                      const ah2x = cx - arrowSize * Math.cos(angle + Math.PI / 6);
                      const ah2y = cy - arrowSize * Math.sin(angle + Math.PI / 6);
                      return (
                        <polygon
                          points={`${cx},${cy} ${ah1x},${ah1y} ${ah2x},${ah2y}`}
                          fill={strokeColor}
                          transform={transform}
                          style={{ pointerEvents: 'none' }}
                        />
                      );
                    })()}
                    {/* Click-capture overlay to finalize */}
                    <rect
                      x={0}
                      y={0}
                      width="100%"
                      height="100%"
                      fill="transparent"
                      onMouseDown={finalize}
                      style={{ cursor: 'crosshair' }}
                    />
                  </g>
                );
              })()}

              {/* TEXT ANNOTATION - Unique identifier for rotation handlers */}
              <ShapeEditHandles
                annotation={annotation}
                isSelected={isSelected || false}
                transform={transform}
                liveEditingTransform={isLiveEditing ? liveEditingTransform : undefined}
                liveEditingGeometry={isLiveEditing ? liveEditingGeometry : undefined}
                onResizeStart={(handleId, start) => {
                  createEditState('resize', start, annotation);
                  (window as any).__shapeEdit.handleId = handleId; // Store handleId for resize operations
                }}
                onResizeDrag={(handleId, pos) => {
                  updateEditState(pos);
                  // Live preview during resize
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;

                  // Use helper function for consistent resize logic
                  const newGeometry = handleRectangleResize(handleId, start, last, orig);

                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(
                    annotation.id,
                    {
                      ...orig.geometry, // Preserve all existing geometry including text
                      ...newGeometry,
                    },
                    orig.transform
                  ); // TEXT ANNOTATION: Preserve transform during live editing
                }}
                onResizeEnd={() => {
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };
                  const handleId = edit.handleId as string;

                  // Use helper function for consistent resize logic
                  const newGeometry = handleRectangleResize(handleId, start, last, orig);

                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: orig.type,
                    geometry: {
                      ...orig.geometry, // Preserve all existing geometry including text
                      x: String(Math.round(parseFloat(newGeometry.x))),
                      y: String(Math.round(parseFloat(newGeometry.y))),
                      width: String(Math.round(parseFloat(newGeometry.width))),
                      height: String(Math.round(parseFloat(newGeometry.height))),
                    },
                    style: orig.style,
                    transform: orig.transform, // Preserve the transform during resize
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during resize
                  });
                  clearEditState();
                }}
                onRotateStart={(start: { x: number; y: number }) => {
                  createEditState('rotate', start, annotation);
                }}
                onRotateDrag={(pos: { x: number; y: number }) => {
                  updateEditState(pos);

                  // Live preview during rotation for TEXT annotation
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(annotation.id, orig.geometry, {
                    ...orig.transform,
                    rotation: String(rotation),
                  });
                }}
                onRotateEnd={() => {
                  const edit = getEditState();
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update the annotation with new rotation
                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: orig.type,
                    geometry: orig.geometry,
                    style: orig.style,
                    transform: {
                      ...orig.transform,
                      rotation: String(Math.round(rotation)),
                    },
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during rotation
                  });

                  clearEditState(); // TEXT ANNOTATION: Use helper function
                }}
              />
            </g>
          );
        }

        case AnnotationType.RECTANGLE: {
          // RECTANGLE ANNOTATION CASE - Unique identifier for this specific case
          const effectiveGeometry = getCurrentEffectiveGeometry();
          const width = parseFloat(effectiveGeometry?.width || '100');
          const height = parseFloat(effectiveGeometry?.height || '50');
          return (
            <g>
              {/* Invisible thicker stroke for easier interaction */}
              <rect
                x={pos.x}
                y={pos.y}
                width={width + shapeSelectionOffset}
                height={height + shapeSelectionOffset}
                fill="none"
                stroke="transparent"
                strokeWidth={shapeSelectionOffset * 2}
                opacity={0}
                transform={transform}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  cursor: shouldDisableInteractions() ? 'default' : isSelected ? 'move' : 'pointer',
                  pointerEvents: shouldDisableInteractions() ? 'none' : 'stroke',
                }}
              />
              {/* Visible shape */}
              <rect
                x={pos.x}
                y={pos.y}
                width={width}
                height={height}
                fill={mergedStyle.fill}
                stroke={mergedStyle.stroke}
                strokeWidth={mergedStyle.strokeWidth}
                strokeDasharray={mergedStyle.strokeDasharray}
                opacity={mergedStyle.opacity}
                transform={transform}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  ...getInteractiveStyle(),
                  ...getPointerEventsStyle(),
                }}
              />
              {annotation.isdimensional && showLineMeasurements && (
                <MeasurementOverlay
                  type={annotation.type}
                  geometry={getCurrentEffectiveGeometry()}
                  transform={transform}
                  color={String(mergedStyle.stroke || '#1976d2')}
                />
              )}

              {/* Separate selection outline - doesn't affect the shape */}
              {isSelected && (
                <rect
                  x={pos.x - 2}
                  y={pos.y - 2}
                  width={width + 4}
                  height={height + 4}
                  fill="none"
                  stroke="rgba(128, 128, 128, 0.8)"
                  strokeWidth="1.5"
                  strokeDasharray="3,3"
                  opacity="1"
                  transform={transform}
                  style={{ pointerEvents: 'none' }}
                />
              )}
              {/* RECTANGLE ANNOTATION - Unique identifier for rotation handlers */}
              <ShapeEditHandles
                annotation={annotation}
                isSelected={isSelected || false}
                transform={transform}
                liveEditingTransform={isLiveEditing ? liveEditingTransform : undefined}
                liveEditingGeometry={isLiveEditing ? liveEditingGeometry : undefined}
                onResizeStart={(handleId, start) => {
                  createEditState('resize', start, annotation);
                  (window as any).__shapeEdit.handleId = handleId; // Store handleId for resize operations
                }}
                onResizeDrag={(handleId, pos) => {
                  updateEditState(pos);
                  // Live preview during resize
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;

                  // Use helper function for consistent resize logic
                  const newGeometry = handleRectangleResize(handleId, start, last, orig);

                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(
                    annotation.id,
                    {
                      ...orig.geometry, // Preserve all existing geometry including text
                      ...newGeometry,
                    },
                    orig.transform
                  ); // RECTANGLE: Preserve transform during live editing
                }}
                onResizeEnd={() => {
                  const edit = getEditState();
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const handleId = edit.handleId as string;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };

                  // Use helper function for consistent resize logic
                  const newGeometry = handleRectangleResize(handleId, start, last, orig);

                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: AnnotationType.RECTANGLE,
                    geometry: {
                      ...orig.geometry,
                      x: String(Math.round(parseFloat(newGeometry.x))),
                      y: String(Math.round(parseFloat(newGeometry.y))),
                      width: String(Math.round(parseFloat(newGeometry.width))),
                      height: String(Math.round(parseFloat(newGeometry.height))),
                    },
                    style: orig.style, // Preserve style
                    transform: orig.transform, // Preserve transform during resize
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during resize
                  });
                  clearEditState(); // RECTANGLE: Use helper function
                }}
                onRotateStart={start => {
                  createEditState('rotate', start, annotation);
                }}
                onRotateDrag={pos => {
                  updateEditState(pos);

                  // Live preview during rotation for RECTANGLE
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(annotation.id, orig.geometry, {
                    ...orig.transform,
                    rotation: String(rotation),
                  });
                }}
                onRotateEnd={() => {
                  const edit = getEditState();
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update the annotation with new rotation
                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: AnnotationType.RECTANGLE,
                    geometry: orig.geometry,
                    style: orig.style,
                    transform: {
                      ...orig.transform,
                      rotation: String(Math.round(rotation)),
                    },
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during rotation
                  });

                  clearEditState(); // RECTANGLE: Use helper function
                }}
              />
            </g>
          );
        }

        case AnnotationType.CIRCLE: {
          // CIRCLE ANNOTATION CASE - Unique identifier for this specific case
          const effectiveGeometry = getCurrentEffectiveGeometry();
          const cx = parseFloat(effectiveGeometry?.cx || '0');
          const cy = parseFloat(effectiveGeometry?.cy || '0');
          const r = parseFloat(effectiveGeometry?.r || '25');

          // Ensure we have valid coordinates
          if (isNaN(cx) || isNaN(cy) || isNaN(r)) {
            console.warn('⚠️ Invalid circle geometry:', { cx, cy, r, effectiveGeometry });
            return null;
          }

          return (
            <g>
              {/* Invisible thicker stroke for easier interaction - much larger area */}
              <circle
                cx={cx}
                cy={cy}
                r={r + shapeSelectionOffset}
                fill="none"
                stroke="transparent"
                strokeWidth="5"
                opacity={0}
                transform={transform}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  cursor: shouldDisableInteractions() ? 'default' : isSelected ? 'move' : 'pointer',
                  pointerEvents: shouldDisableInteractions() ? 'none' : 'all',
                  ...getPointerEventsStyle(),
                }}
              />
              {/* Visible shape - no event handlers, only visual */}
              <circle
                cx={cx}
                cy={cy}
                r={r}
                fill={mergedStyle.fill}
                stroke={mergedStyle.stroke}
                strokeWidth={mergedStyle.strokeWidth}
                strokeDasharray={mergedStyle.strokeDasharray}
                opacity={mergedStyle.opacity}
                transform={transform}
                style={{
                  pointerEvents: 'none', // No events on visible shape
                }}
              />
              {annotation.isdimensional && showLineMeasurements && (
                <MeasurementOverlay
                  type={annotation.type}
                  geometry={getCurrentEffectiveGeometry()}
                  transform={transform}
                  color={String(mergedStyle.stroke || '#1976d2')}
                />
              )}

              {/* Separate selection outline - doesn't affect the shape */}
              {isSelected && (
                <circle
                  cx={cx}
                  cy={cy}
                  r={r + 2}
                  fill="none"
                  stroke="rgba(128, 128, 128, 0.8)"
                  strokeWidth="1.5"
                  strokeDasharray="3,3"
                  opacity="1"
                  transform={transform}
                  style={{ pointerEvents: 'none' }}
                />
              )}
              <ShapeEditHandles
                annotation={annotation}
                isSelected={isSelected || false}
                transform={transform}
                liveEditingTransform={isLiveEditing ? liveEditingTransform : undefined}
                liveEditingGeometry={isLiveEditing ? liveEditingGeometry : undefined}
                onResizeStart={(handleId: string, start: { x: number; y: number }) => {
                  (window as any).__shapeEdit = {
                    mode: 'resize',
                    handleId,
                    start,
                    last: start,
                    original: annotation,
                  };
                }}
                onResizeDrag={(handleId: string, pos: { x: number; y: number }) => {
                  (window as any).__shapeEdit = { ...(window as any).__shapeEdit, last: pos };
                  // Live preview during resize
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const cx0 = parseFloat(orig.geometry?.cx || '0');
                  const cy0 = parseFloat(orig.geometry?.cy || '0');
                  const last = pos;

                  // Use center-based resize for consistent behavior
                  const transformedLast = last;

                  // All handles maintain circle shape - no conversion to ellipse
                  const rNew = Math.max(
                    1,
                    Math.hypot(transformedLast.x - cx0, transformedLast.y - cy0)
                  );

                  const newGeometry = {
                    ...orig.geometry,
                    cx: String(cx0),
                    cy: String(cy0),
                    r: String(rNew),
                  };

                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(annotation.id, newGeometry, annotation.transform);
                }}
                onResizeEnd={() => {
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const cx0 = parseFloat(orig.geometry?.cx || '0');
                  const cy0 = parseFloat(orig.geometry?.cy || '0');
                  const last = edit.last as { x: number; y: number };

                  // Use center-based resize for consistent behavior
                  const transformedLast = last;

                  // All handles maintain circle shape - no conversion to ellipse
                  const rNew = Math.max(
                    1,
                    Math.hypot(transformedLast.x - cx0, transformedLast.y - cy0)
                  );

                  // Create new geometry, removing ellipse properties for circle
                  const circleGeometry = { ...orig.geometry };
                  delete circleGeometry.rx; // Remove ellipse properties
                  delete circleGeometry.ry;

                  const finalGeometry = {
                    ...circleGeometry,
                    cx: String(Math.round(cx0)),
                    cy: String(Math.round(cy0)),
                    r: String(Math.round(rNew)),
                  };
                  const finalType = AnnotationType.CIRCLE;

                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: finalType,
                    geometry: finalGeometry,
                    style: orig.style,
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during resize
                  });
                  (window as any).__shapeEdit = null;
                }}
                onRotateStart={(start: { x: number; y: number }) => {
                  createEditState('rotate', start, annotation);
                }}
                onRotateDrag={(pos: { x: number; y: number }) => {
                  updateEditState(pos);

                  // Live preview during rotation for CIRCLE
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(annotation.id, orig.geometry, {
                    ...orig.transform,
                    rotation: String(rotation),
                  });
                }}
                onRotateEnd={() => {
                  const edit = getEditState();
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update the annotation with new rotation
                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: orig.type,
                    geometry: orig.geometry,
                    style: orig.style,
                    transform: {
                      ...orig.transform,
                      rotation: String(Math.round(rotation)),
                    },
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during rotation
                  });

                  clearEditState(); // Use helper function
                }}
              />
            </g>
          );
        }

        case AnnotationType.ELLIPSE: {
          const effectiveGeometry = getCurrentEffectiveGeometry();
          const cx = parseFloat(effectiveGeometry?.cx || '0');
          const cy = parseFloat(effectiveGeometry?.cy || '0');
          const rx = parseFloat(effectiveGeometry?.rx || '40');
          const ry = parseFloat(effectiveGeometry?.ry || '25');

          // Ensure we have valid coordinates
          if (isNaN(cx) || isNaN(cy) || isNaN(rx) || isNaN(ry)) {
            console.warn('⚠️ Invalid ellipse geometry:', { cx, cy, rx, ry, effectiveGeometry });
            return null;
          }

          return (
            <g>
              {/* Invisible interaction area - much larger than visible ellipse */}
              <ellipse
                cx={cx}
                cy={cy}
                rx={rx + shapeSelectionOffset}
                ry={ry + shapeSelectionOffset}
                fill="transparent"
                opacity={0}
                transform={transform}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  cursor: shouldDisableInteractions() ? 'default' : isSelected ? 'move' : 'pointer',
                  pointerEvents: shouldDisableInteractions() ? 'none' : 'all',
                  ...getPointerEventsStyle(),
                }}
              />

              {/* Visible shape - no event handlers, only visual */}
              <ellipse
                cx={cx}
                cy={cy}
                rx={rx}
                ry={ry}
                fill={mergedStyle.fill}
                stroke={mergedStyle.stroke}
                strokeWidth={mergedStyle.strokeWidth}
                strokeDasharray={mergedStyle.strokeDasharray}
                opacity={mergedStyle.opacity}
                transform={transform}
                style={{
                  pointerEvents: 'none', // No events on visible shape
                }}
              />
              {annotation.isdimensional && showLineMeasurements && (
                <MeasurementOverlay
                  type={annotation.type}
                  geometry={getCurrentEffectiveGeometry()}
                  transform={transform}
                  color={String(mergedStyle.stroke || '#1976d2')}
                />
              )}

              {/* Separate selection outline - doesn't affect the shape */}
              {isSelected && (
                <ellipse
                  cx={cx}
                  cy={cy}
                  rx={rx + 2}
                  ry={ry + 2}
                  fill="none"
                  stroke="rgba(128, 128, 128, 0.8)"
                  strokeWidth="1.5"
                  strokeDasharray="3,3"
                  opacity="1"
                  transform={transform}
                  style={{ pointerEvents: 'none' }}
                />
              )}
              <ShapeEditHandles
                annotation={annotation}
                isSelected={isSelected || false}
                transform={transform}
                liveEditingTransform={isLiveEditing ? liveEditingTransform : undefined}
                liveEditingGeometry={isLiveEditing ? liveEditingGeometry : undefined}
                onResizeStart={(handleId: string, start: { x: number; y: number }) => {
                  createEditState('resize', start, annotation);
                  (window as any).__shapeEdit.handleId = handleId; // Store handleId for resize operations
                }}
                onResizeDrag={(handleId: string, pos: { x: number; y: number }) => {
                  updateEditState(pos);
                  // Live preview during resize
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const cx0 = parseFloat(orig.geometry?.cx || '0');
                  const cy0 = parseFloat(orig.geometry?.cy || '0');
                  const last = pos;

                  // Calculate new rx/ry based on handle
                  // If coming from a circle, preserve the original radius
                  let rx = parseFloat(orig.geometry?.rx || orig.geometry?.r || '40');
                  let ry = parseFloat(orig.geometry?.ry || orig.geometry?.r || '25');

                  if (['n', 's'].includes(handleId)) {
                    ry = Math.max(1, Math.abs(last.y - cy0));
                  } else if (['e', 'w'].includes(handleId)) {
                    rx = Math.max(1, Math.abs(last.x - cx0));
                  } else if (['nw', 'ne', 'sw', 'se'].includes(handleId)) {
                    // Corner handles: maintain aspect ratio
                    const distance = Math.hypot(last.x - cx0, last.y - cy0);
                    const ratio = rx / ry;
                    if (ratio >= 1) {
                      rx = distance;
                      ry = distance / ratio;
                    } else {
                      ry = distance;
                      rx = distance * ratio;
                    }
                  }

                  // Create new geometry for live editing, removing 'r' property if it exists
                  const liveGeometry = { ...orig.geometry };
                  delete liveGeometry.r; // Remove circle radius property for ellipse

                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(
                    annotation.id,
                    {
                      ...liveGeometry,
                      cx: String(cx0),
                      cy: String(cy0),
                      rx: String(rx),
                      ry: String(ry),
                    },
                    annotation.transform
                  );
                }}
                onResizeEnd={() => {
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const handleId = edit.handleId as string;
                  const cx0 = parseFloat(orig.geometry?.cx || '0');
                  const cy0 = parseFloat(orig.geometry?.cy || '0');
                  const last = edit.last as { x: number; y: number };

                  // If coming from a circle, preserve the original radius
                  let rx = parseFloat(orig.geometry?.rx || orig.geometry?.r || '40');
                  let ry = parseFloat(orig.geometry?.ry || orig.geometry?.r || '25');

                  if (['n', 's'].includes(handleId)) {
                    ry = Math.max(1, Math.abs(last.y - cy0));
                  } else if (['e', 'w'].includes(handleId)) {
                    rx = Math.max(1, Math.abs(last.x - cx0));
                  } else if (['nw', 'ne', 'sw', 'se'].includes(handleId)) {
                    const distance = Math.hypot(last.x - cx0, last.y - cy0);
                    const ratio = rx / ry;
                    if (ratio >= 1) {
                      rx = distance;
                      ry = distance / ratio;
                    } else {
                      ry = distance;
                      rx = distance * ratio;
                    }
                  }

                  // Create new geometry, removing 'r' property if it exists (circle to ellipse conversion)
                  const newGeometry = { ...orig.geometry };
                  delete newGeometry.r; // Remove circle radius property

                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: AnnotationType.ELLIPSE,
                    geometry: {
                      ...newGeometry,
                      cx: String(Math.round(cx0)),
                      cy: String(Math.round(cy0)),
                      rx: String(Math.round(rx)),
                      ry: String(Math.round(ry)),
                    },
                    style: orig.style,
                    transform: orig.transform, // Preserve transform during resize
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during resize
                  });
                  clearEditState(); // Use helper function
                }}
                onRotateStart={(start: { x: number; y: number }) => {
                  createEditState('rotate', start, annotation);
                }}
                onRotateDrag={(pos: { x: number; y: number }) => {
                  updateEditState(pos);

                  // Live preview during rotation for ELLIPSE
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(annotation.id, orig.geometry, {
                    ...orig.transform,
                    rotation: String(rotation),
                  });
                }}
                onRotateEnd={() => {
                  const edit = getEditState();
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update the annotation with new rotation
                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: orig.type,
                    geometry: orig.geometry,
                    style: orig.style,
                    transform: {
                      ...orig.transform,
                      rotation: String(Math.round(rotation)),
                    },
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during rotation
                  });

                  clearEditState(); // Use helper function
                }}
              />
            </g>
          );
        }

        case AnnotationType.LINE: {
          const effectiveGeometry = getCurrentEffectiveGeometry();
          const x1 = parseFloat(effectiveGeometry?.x1 || pos.x.toString());
          const y1 = parseFloat(effectiveGeometry?.y1 || pos.y.toString());
          const x2 = parseFloat(effectiveGeometry?.x2 || (pos.x + 100).toString());
          const y2 = parseFloat(effectiveGeometry?.y2 || pos.y.toString());
          return (
            <g>
              {/* Invisible thicker stroke for easier interaction */}
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="transparent"
                strokeWidth="20"
                opacity={0}
                transform={transform}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  cursor: shouldDisableInteractions() ? 'default' : isSelected ? 'move' : 'pointer',
                  pointerEvents: shouldDisableInteractions() ? 'none' : 'stroke',
                }}
              />
              {/* Visible line */}
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={mergedStyle.stroke}
                strokeWidth={mergedStyle.strokeWidth}
                strokeDasharray={mergedStyle.strokeDasharray}
                opacity={mergedStyle.opacity}
                transform={transform}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  ...getInteractiveStyle(),
                  ...getPointerEventsStyle(),
                }}
              />
              {annotation.isdimensional && showLineMeasurements && (
                <MeasurementOverlay
                  type={annotation.type}
                  geometry={getCurrentEffectiveGeometry()}
                  transform={transform}
                  color={String(mergedStyle.stroke || '#1976d2')}
                />
              )}

              {/* Separate selection outline - doesn't affect the shape */}
              {isSelected && (
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="rgba(128, 128, 128, 0.8)"
                  strokeWidth="3.5"
                  strokeDasharray="3,3"
                  opacity="1"
                  transform={transform}
                  style={{ pointerEvents: 'none' }}
                />
              )}
              <ShapeEditHandles
                annotation={annotation}
                isSelected={isSelected || false}
                onResizeStart={(handleId: string, start: { x: number; y: number }) => {
                  (window as any).__shapeEdit = {
                    mode: 'resize',
                    handleId,
                    start,
                    last: start,
                    original: annotation,
                  };
                }}
                onResizeDrag={(handleId: string, pos: { x: number; y: number }) => {
                  (window as any).__shapeEdit = { ...(window as any).__shapeEdit, last: pos };
                  // Live preview during resize
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const which = edit.handleId as string;
                  const last = pos;

                  // Use center-based resize for consistent behavior
                  const transformedLast = last;

                  let newGeometry: any = { ...orig.geometry };
                  if (which === 'start') {
                    newGeometry.x1 = String(transformedLast.x);
                    newGeometry.y1 = String(transformedLast.y);
                  } else {
                    newGeometry.x2 = String(transformedLast.x);
                    newGeometry.y2 = String(transformedLast.y);
                  }

                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(annotation.id, newGeometry, annotation.transform);
                }}
                onResizeEnd={() => {
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const which = edit.handleId as string;
                  const last = edit.last as { x: number; y: number };

                  // Use center-based resize for consistent behavior
                  const transformedLast = last;

                  // Preserve all original geometry and only update the changed coordinates
                  const newGeometry: any = { ...orig.geometry };
                  if (which === 'start') {
                    newGeometry.x1 = String(Math.round(transformedLast.x));
                    newGeometry.y1 = String(Math.round(transformedLast.y));
                  } else {
                    newGeometry.x2 = String(Math.round(transformedLast.x));
                    newGeometry.y2 = String(Math.round(transformedLast.y));
                  }

                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: AnnotationType.LINE,
                    geometry: newGeometry,
                    style: orig.style, // Preserve style
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during resize
                  });
                  (window as any).__shapeEdit = null;
                }}
              />
            </g>
          );
        }

        case AnnotationType.HIGHLIGHTER: {
          const effectiveGeometry = getCurrentEffectiveGeometry();
          const width = parseFloat(effectiveGeometry?.width || '100');
          const height = parseFloat(effectiveGeometry?.height || '20');
          return (
            <g>
              {/* Invisible thicker stroke for easier interaction */}
              <rect
                x={pos.x}
                y={pos.y}
                width={width + shapeSelectionOffset}
                height={height + shapeSelectionOffset}
                fill="none"
                stroke="transparent"
                strokeWidth={shapeSelectionOffset * 2}
                opacity={0}
                transform={transform}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  cursor: shouldDisableInteractions() ? 'default' : isSelected ? 'move' : 'pointer',
                  pointerEvents: shouldDisableInteractions() ? 'none' : 'fill',
                  transition: 'opacity 0.2s ease',
                }}
                onMouseEnter={
                  shouldDisableInteractions()
                    ? undefined
                    : e => {
                        e.currentTarget.style.opacity = '0.1';
                        e.currentTarget.style.stroke = 'rgba(255, 255, 0, 0.3)';
                      }
                }
                onMouseLeave={
                  shouldDisableInteractions()
                    ? undefined
                    : e => {
                        e.currentTarget.style.opacity = '0';
                        e.currentTarget.style.stroke = 'transparent';
                      }
                }
              />
              {/* Visible highlighter */}
              <rect
                x={pos.x}
                y={pos.y}
                width={width}
                height={height}
                fill={mergedStyle.fill || 'rgba(255, 255, 0, 0.3)'}
                stroke="rgba(255, 255, 0, 0.6)"
                strokeWidth="0"
                opacity={mergedStyle.opacity}
                transform={transform}
                style={{
                  mixBlendMode: 'multiply',
                  ...getInteractiveStyle(),
                  transition: 'stroke 0.2s ease, fill 0.2s ease',
                  ...getPointerEventsStyle(),
                }}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                onMouseEnter={
                  shouldDisableInteractions()
                    ? undefined
                    : e => {
                        if (!isSelected) {
                          e.currentTarget.style.stroke = 'rgba(255, 255, 0, 0.8)';
                          e.currentTarget.style.strokeWidth = '1';
                        }
                      }
                }
                onMouseLeave={
                  shouldDisableInteractions()
                    ? undefined
                    : e => {
                        if (!isSelected) {
                          e.currentTarget.style.stroke = 'rgba(255, 255, 0, 0.6)';
                          e.currentTarget.style.strokeWidth = '0';
                        }
                      }
                }
              />
              {annotation.isdimensional && showLineMeasurements && (
                <MeasurementOverlay
                  type={annotation.type}
                  geometry={getCurrentEffectiveGeometry()}
                  transform={transform}
                  color={String(mergedStyle.stroke || '#1976d2')}
                />
              )}

              {/* Separate selection outline - doesn't affect the shape */}
              {isSelected && (
                <rect
                  x={pos.x - 2}
                  y={pos.y - 2}
                  width={width + 4}
                  height={height + 4}
                  fill="none"
                  stroke="rgba(128, 128, 128, 0.8)"
                  strokeWidth="1.5"
                  strokeDasharray="3,3"
                  opacity="1"
                  transform={transform}
                  style={{ pointerEvents: 'none' }}
                />
              )}
              <ShapeEditHandles
                annotation={annotation}
                isSelected={isSelected || false}
                transform={transform}
                liveEditingTransform={isLiveEditing ? liveEditingTransform : undefined}
                liveEditingGeometry={isLiveEditing ? liveEditingGeometry : undefined}
                onResizeStart={(handleId, start) => {
                  (window as any).__shapeEdit = {
                    mode: 'resize',
                    handleId,
                    start,
                    last: start,
                    original: annotation,
                  };
                }}
                onResizeDrag={(handleId, pos) => {
                  (window as any).__shapeEdit = { ...(window as any).__shapeEdit, last: pos };
                  // Live preview during resize
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;
                  const dx = last.x - start.x;
                  const dy = last.y - start.y;
                  let x = parseFloat(orig.geometry?.x || '0');
                  let y = parseFloat(orig.geometry?.y || '0');
                  let w = parseFloat(orig.geometry?.width || '0');
                  let h = parseFloat(orig.geometry?.height || '0');

                  if (handleId.includes('n')) {
                    y += dy;
                    h -= dy;
                  }
                  if (handleId.includes('s')) {
                    h += dy;
                  }
                  if (handleId.includes('w')) {
                    x += dx;
                    w -= dx;
                  }
                  if (handleId.includes('e')) {
                    w += dx;
                  }

                  w = Math.max(1, w);
                  h = Math.max(1, h);

                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(
                    annotation.id,
                    {
                      ...orig.geometry,
                      x: String(x),
                      y: String(y),
                      width: String(w),
                      height: String(h),
                    },
                    annotation.transform
                  );
                }}
                onResizeEnd={() => {
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const handleId = edit.handleId as string;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };
                  const dx = last.x - start.x;
                  const dy = last.y - start.y;
                  let x = parseFloat(orig.geometry?.x || '0');
                  let y = parseFloat(orig.geometry?.y || '0');
                  let w = parseFloat(orig.geometry?.width || '0');
                  let h = parseFloat(orig.geometry?.height || '0');

                  if (handleId.includes('n')) {
                    y += dy;
                    h -= dy;
                  }
                  if (handleId.includes('s')) {
                    h += dy;
                  }
                  if (handleId.includes('w')) {
                    x += dx;
                    w -= dx;
                  }
                  if (handleId.includes('e')) {
                    w += dx;
                  }

                  w = Math.max(1, w);
                  h = Math.max(1, h);

                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: AnnotationType.HIGHLIGHTER,
                    geometry: {
                      ...orig.geometry,
                      x: String(Math.round(x)),
                      y: String(Math.round(y)),
                      width: String(Math.round(w)),
                      height: String(Math.round(h)),
                    },
                    style: orig.style, // Preserve style
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during resize
                  });
                  (window as any).__shapeEdit = null;
                }}
                onRotateStart={start => {
                  createEditState('rotate', start, annotation);
                }}
                onRotateDrag={pos => {
                  updateEditState(pos);

                  // Live preview during rotation for HIGHLIGHTER
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(annotation.id, orig.geometry, {
                    ...orig.transform,
                    rotation: String(rotation),
                  });
                }}
                onRotateEnd={() => {
                  const edit = getEditState();
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update the annotation with new rotation
                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: orig.type,
                    geometry: orig.geometry,
                    style: orig.style,
                    transform: {
                      ...orig.transform,
                      rotation: String(Math.round(rotation)),
                    },
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during rotation
                  });

                  clearEditState(); // Use helper function
                }}
              />
            </g>
          );
        }

        case AnnotationType.ARROW: {
          const effectiveGeometry = getCurrentEffectiveGeometry();
          const x1 = parseFloat(effectiveGeometry?.x1 || pos.x.toString());
          const y1 = parseFloat(effectiveGeometry?.y1 || pos.y.toString());
          const x2 = parseFloat(effectiveGeometry?.x2 || (pos.x + 100).toString());
          const y2 = parseFloat(effectiveGeometry?.y2 || pos.y.toString());

          // Calculate arrow head using utility function
          const arrowGeometry = calculateArrowHeadGeometry(x1, y1, x2, y2);
          const { lineEndX, lineEndY, arrowHead1X, arrowHead1Y, arrowHead2X, arrowHead2Y } =
            arrowGeometry;

          return (
            <g transform={transform}>
              {/* Invisible thicker stroke for easier interaction */}
              <line
                x1={x1}
                y1={y1}
                x2={lineEndX}
                y2={lineEndY}
                stroke="transparent"
                strokeWidth="20"
                opacity={0}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  cursor: shouldDisableInteractions() ? 'default' : isSelected ? 'move' : 'pointer',
                  pointerEvents: shouldDisableInteractions() ? 'none' : 'stroke',
                }}
              />
              {/* Visible arrow line */}
              <line
                x1={x1}
                y1={y1}
                x2={lineEndX}
                y2={lineEndY}
                stroke={mergedStyle.stroke}
                strokeWidth={mergedStyle.strokeWidth}
                opacity={mergedStyle.opacity}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  ...getInteractiveStyle(),
                  ...getPointerEventsStyle(),
                }}
              />
              {/* Visible arrow head */}
              <polygon
                points={`${x2},${y2} ${arrowHead1X},${arrowHead1Y} ${arrowHead2X},${arrowHead2Y}`}
                fill={mergedStyle.stroke}
                opacity={mergedStyle.opacity}
                onClick={shouldDisableInteractions() ? undefined : handleShapeClick}
                onMouseDown={shouldDisableInteractions() ? undefined : handleShapeMouseDown}
                onContextMenu={shouldDisableInteractions() ? undefined : handleContextMenu}
                style={{
                  ...getInteractiveStyle(),
                  ...getPointerEventsStyle(),
                }}
              />

              {annotation.isdimensional && showLineMeasurements && (
                <MeasurementOverlay
                  type={annotation.type}
                  geometry={getCurrentEffectiveGeometry()}
                  transform={transform}
                  color={String(mergedStyle.stroke || '#1976d2')}
                />
              )}

              {/* Separate selection outline - doesn't affect the shape */}
              {isSelected && (
                <g>
                  <line
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="rgba(128, 128, 128, 0.8)"
                    strokeWidth="3.5"
                    strokeDasharray="3,3"
                    opacity="1"
                    transform={transform}
                    style={{ pointerEvents: 'none' }}
                  />
                  <polygon
                    points={`${x2},${y2} ${arrowHead1X},${arrowHead1Y} ${arrowHead2X},${arrowHead2Y}`}
                    fill="none"
                    stroke="rgba(128, 128, 128, 0.8)"
                    strokeWidth="1.5"
                    strokeDasharray="3,3"
                    opacity="1"
                    transform={transform}
                    style={{ pointerEvents: 'none' }}
                  />
                </g>
              )}
              <ShapeEditHandles
                annotation={annotation}
                isSelected={isSelected || false}
                transform={transform}
                liveEditingTransform={isLiveEditing ? liveEditingTransform : undefined}
                liveEditingGeometry={isLiveEditing ? liveEditingGeometry : undefined}
                onResizeStart={(handleId: string, start: { x: number; y: number }) => {
                  (window as any).__shapeEdit = {
                    mode: 'resize',
                    handleId,
                    start,
                    last: start,
                    original: annotation,
                  };
                }}
                onResizeDrag={(handleId: string, pos: { x: number; y: number }) => {
                  (window as any).__shapeEdit = { ...(window as any).__shapeEdit, last: pos };
                  // Live preview during resize
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const which = edit.handleId as string;
                  const last = pos;

                  let newGeometry: any = { ...orig.geometry };
                  if (which === 'start') {
                    newGeometry.x1 = String(last.x);
                    newGeometry.y1 = String(last.y);
                  } else {
                    newGeometry.x2 = String(last.x);
                    newGeometry.y2 = String(last.y);
                  }

                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(annotation.id, newGeometry, annotation.transform);
                }}
                onResizeEnd={() => {
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const which = edit.handleId as string;
                  const last = edit.last as { x: number; y: number };

                  // Preserve all original geometry and only update the changed coordinates
                  const newGeometry: any = { ...orig.geometry };
                  if (which === 'start') {
                    newGeometry.x1 = String(Math.round(last.x));
                    newGeometry.y1 = String(Math.round(last.y));
                  } else {
                    newGeometry.x2 = String(Math.round(last.x));
                    newGeometry.y2 = String(Math.round(last.y));
                  }

                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: AnnotationType.ARROW,
                    geometry: newGeometry,
                    style: orig.style, // Preserve style
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during resize
                  });
                  (window as any).__shapeEdit = null;
                }}
              />
            </g>
          );
        }

        case AnnotationType.POLYLINE:
        case AnnotationType.POLYGON: {
          const effectiveGeometry = getCurrentEffectiveGeometry();
          const points =
            effectiveGeometry?.points ||
            `${pos.x},${pos.y} ${pos.x + 50},${pos.y + 50} ${pos.x + 100},${pos.y}`;
          const Element = annotationType === AnnotationType.POLYLINE ? 'polyline' : 'polygon';
          return (
            <g>
              {/* Invisible thicker stroke for easier interaction */}
              {React.createElement(Element, {
                points,
                fill: 'none',
                stroke: 'transparent',
                strokeWidth: '20',
                opacity: 0,
                transform,
                onClick: shouldDisableInteractions() ? undefined : handleShapeClick,
                onMouseDown: shouldDisableInteractions() ? undefined : handleShapeMouseDown,
                onContextMenu: shouldDisableInteractions() ? undefined : handleContextMenu,
                style: {
                  cursor: shouldDisableInteractions() ? 'default' : isSelected ? 'move' : 'pointer',
                  pointerEvents: shouldDisableInteractions() ? 'none' : 'stroke',
                  ...getPointerEventsStyle(),
                },
              })}
              {/* Visible shape */}
              {React.createElement(Element, {
                points,
                fill: annotationType === AnnotationType.POLYGON ? mergedStyle.fill : 'none',
                stroke: mergedStyle.stroke,
                strokeWidth: mergedStyle.strokeWidth,
                strokeDasharray: mergedStyle.strokeDasharray,
                opacity: mergedStyle.opacity,
                transform,
                onClick: shouldDisableInteractions() ? undefined : handleShapeClick,
                onMouseDown: shouldDisableInteractions() ? undefined : handleShapeMouseDown,
                onContextMenu: shouldDisableInteractions() ? undefined : handleContextMenu,
                style: {
                  ...getInteractiveStyle(),
                  ...getPointerEventsStyle(),
                },
              })}

              {annotation.isdimensional && showLineMeasurements && (
                <MeasurementOverlay
                  type={annotation.type}
                  geometry={getCurrentEffectiveGeometry()}
                  transform={transform}
                  color={String(mergedStyle.stroke || '#1976d2')}
                />
              )}

              {/* Separate selection outline - doesn't affect the shape */}
              {isSelected &&
                React.createElement(Element, {
                  points,
                  fill: 'none',
                  stroke: 'rgba(128, 128, 128, 0.8)',
                  strokeWidth: '3.5',
                  strokeDasharray: '3,3',
                  opacity: '1',
                  transform,
                  style: { pointerEvents: 'none' },
                })}

              <ShapeEditHandles
                annotation={annotation}
                isSelected={isSelected || false}
                transform={transform}
                liveEditingTransform={isLiveEditing ? liveEditingTransform : undefined}
                liveEditingGeometry={isLiveEditing ? liveEditingGeometry : undefined}
                onResizeStart={(handleId: string, start: { x: number; y: number }) => {
                  (window as any).__shapeEdit = {
                    mode: 'resize',
                    handleId,
                    start,
                    last: start,
                    original: annotation,
                  };
                }}
                onResizeDrag={(handleId: string, pos: { x: number; y: number }) => {
                  (window as any).__shapeEdit = { ...(window as any).__shapeEdit, last: pos };
                  // Live preview during resize
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;

                  let newGeometry: any;

                  // Handle vertex point resizing
                  const pointMatch = edit.handleId?.match(/^point-(\d+)$/);
                  if (pointMatch) {
                    const idx = parseInt(pointMatch[1], 10);
                    const pts = (orig.geometry?.points || '')
                      .split(' ')
                      .map(p => p.split(',').map(Number));
                    if (idx >= 0 && idx < pts.length) {
                      pts[idx] = [last.x, last.y];
                      const pointsStr = pts.map(([px, py]) => `${px},${py}`).join(' ');
                      newGeometry = {
                        ...orig.geometry,
                        points: pointsStr,
                      };
                    }
                  }
                  // Handle midpoint resizing
                  else if (edit.handleId?.startsWith('midpoint-')) {
                    newGeometry = handlePolygonMidpointResize(edit.handleId, start, last, orig);
                  }

                  if (newGeometry) {
                    const store = (
                      require('../annotations/annotationStore') as any
                    ).useAnnotationStore?.getState?.();
                    store?.setLiveEditingShape?.(annotation.id, newGeometry, annotation.transform);
                  }
                }}
                onResizeEnd={() => {
                  const edit = (window as any).__shapeEdit;
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };

                  let newGeometry: any;

                  // Handle vertex point resizing
                  const pointMatch = edit.handleId?.match(/^point-(\d+)$/);
                  if (pointMatch) {
                    const idx = parseInt(pointMatch[1], 10);
                    const pts = (orig.geometry?.points || '')
                      .split(' ')
                      .map(p => p.split(',').map(Number));
                    if (idx >= 0 && idx < pts.length) {
                      pts[idx] = [Math.round(last.x), Math.round(last.y)];
                      const pointsStr = pts.map(([px, py]) => `${px},${py}`).join(' ');
                      newGeometry = { ...orig.geometry, points: pointsStr };
                    }
                  }
                  // Handle midpoint resizing
                  else if (edit.handleId?.startsWith('midpoint-')) {
                    newGeometry = handlePolygonMidpointResize(edit.handleId, start, last, orig);
                    // Round coordinates for clean values
                    if (newGeometry?.points) {
                      const pts = newGeometry.points.split(' ').map((p: string) => {
                        const [x, y] = p.split(',').map(Number);
                        return `${Math.round(x)},${Math.round(y)}`;
                      });
                      newGeometry.points = pts.join(' ');
                    }
                  }

                  if (newGeometry) {
                    updateShape(orig.id, {
                      id: orig.id,
                      objectId: orig.objectId,
                      type: orig.type || AnnotationType.POLYGON,
                      geometry: newGeometry,
                      style: orig.style, // Preserve style
                      isdimensional: orig.isdimensional, // Preserve dimensional flag during resize
                    });
                  }
                  (window as any).__shapeEdit = null;
                }}
                onRotateStart={(start: { x: number; y: number }) => {
                  createEditState('rotate', start, annotation);
                }}
                onRotateDrag={(pos: { x: number; y: number }) => {
                  updateEditState(pos);

                  // Live preview during rotation for POLYLINE
                  const edit = getEditState();
                  if (!edit) return;
                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = pos;

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(annotation.id, orig.geometry, {
                    ...orig.transform,
                    rotation: String(rotation),
                  });
                }}
                onRotateEnd={() => {
                  const edit = getEditState();
                  if (!edit) return;

                  // Clear live editing state
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setLiveEditingShape?.(null, null, null);

                  const orig = edit.original as EnhancedAnnotation;
                  const start = edit.start as { x: number; y: number };
                  const last = edit.last as { x: number; y: number };

                  // Use helper function for consistent rotation calculation
                  const { rotation } = calculateRotationAngle(
                    start,
                    last,
                    orig,
                    snapMode,
                    angleOptions
                  );

                  // Update the annotation with new rotation
                  updateShape(orig.id, {
                    id: orig.id,
                    objectId: orig.objectId,
                    type: orig.type,
                    geometry: orig.geometry,
                    style: orig.style,
                    transform: {
                      ...orig.transform,
                      rotation: String(Math.round(rotation)),
                    },
                    isdimensional: orig.isdimensional, // Preserve dimensional flag during rotation
                  });

                  clearEditState(); // Use helper function
                }}
              />
            </g>
          );
        }

        default:
          return null;
      }
    };

    // Helper function to safely get annotation type name
    const getCurrentAnnotationTypeName = () => {
      return getAnnotationTypeName(annotation.type);
    };

    return (
      <g className={`annotation annotation-${getCurrentAnnotationTypeName()}`}>
        {renderAnnotation()}
      </g>
    );
  }
);

export default SVGAnnotationRenderer;
